// useEffectiveBrand — read the brand that should render for the current view.
//
// For v1 this is just the current user's own brand_configs row (single-tenant
// per owner). Future iterations can resolve a custom-domain visitor's brand
// without auth by looking up `brand_configs.custom_domain` against the
// hostname; for now non-Studio (and unauth) users get platform defaults.
//
// The hook also signs the logo storage path so callers get an `<img src>`-
// ready URL. Storage signing is debounced via React Query staleTime so the
// page doesn't refetch the signed URL on every render.

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useBrandConfig, type BrandConfigRow } from './useBrandConfig';
import { useEntitlement } from './useEntitlement';

export interface EffectiveBrand {
  logoUrl: string | null; // signed URL ready for <img src>; null when not customized
  primaryColor: string | null;
  accentColor: string | null;
  isCustomized: boolean; // false ⇒ platform defaults, BrandProvider is a no-op
  isLoading: boolean;
}

const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour
const SIGNED_URL_STALE_MS = 50 * 60 * 1000; // refetch ~10 min before expiry

async function signLogoUrl(path: string | null): Promise<string | null> {
  if (!path) return null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).storage
    .from('brand-logos')
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
  if (error || !data?.signedUrl) return null;
  return data.signedUrl as string;
}

function selectFromRow(
  row: BrandConfigRow | null,
  signedLogo: string | null,
): Omit<EffectiveBrand, 'isLoading'> {
  if (!row) {
    return {
      logoUrl: null,
      primaryColor: null,
      accentColor: null,
      isCustomized: false,
    };
  }
  const hasAnyCustomization = !!row.logo_url || !!row.primary_color || !!row.accent_color;
  return {
    logoUrl: signedLogo,
    primaryColor: row.primary_color,
    accentColor: row.accent_color,
    isCustomized: hasAnyCustomization,
  };
}

export function useEffectiveBrand(): EffectiveBrand {
  const { isStudio, isLoading: entitlementLoading } = useEntitlement();
  const { config, isLoading: configLoading } = useBrandConfig();

  // Sign the logo separately so we can cache the signed URL across renders
  // and refresh it well before the 1-hour TTL elapses (debounced via stale
  // time — no per-render re-signing).
  const logoPath = isStudio ? (config?.logo_url ?? null) : null;
  const signedLogoQuery = useQuery({
    queryKey: ['effective-brand', 'signed-logo', logoPath],
    queryFn: () => signLogoUrl(logoPath),
    enabled: !!logoPath,
    staleTime: SIGNED_URL_STALE_MS,
  });

  // Coalesce the row + signed URL into a single snapshot. Using local state
  // rather than recomputing every render means downstream consumers (e.g.
  // BrandProvider's effect that writes CSS variables) only fire when the
  // values actually change — no color-picker flicker.
  const [snapshot, setSnapshot] = useState<Omit<EffectiveBrand, 'isLoading'>>({
    logoUrl: null,
    primaryColor: null,
    accentColor: null,
    isCustomized: false,
  });

  useEffect(() => {
    // Non-Studio short-circuits to defaults — never apply a stored brand
    // when entitlement lapses.
    if (!isStudio) {
      setSnapshot({
        logoUrl: null,
        primaryColor: null,
        accentColor: null,
        isCustomized: false,
      });
      return;
    }
    const next = selectFromRow(config ?? null, signedLogoQuery.data ?? null);
    setSnapshot((prev) => {
      if (
        prev.logoUrl === next.logoUrl &&
        prev.primaryColor === next.primaryColor &&
        prev.accentColor === next.accentColor &&
        prev.isCustomized === next.isCustomized
      ) {
        return prev;
      }
      return next;
    });
  }, [isStudio, config, signedLogoQuery.data]);

  return {
    ...snapshot,
    isLoading: entitlementLoading || configLoading,
  };
}
